import React, { useState, useEffect } from 'react';
import { Modal, Btn, Input, Select, Textarea, Badge, DatePicker } from './UI';
import { fmtDate, fmt, fmtNum } from '../utils';
import * as api from '../api';

export default function DMRTransactionModal({
  onClose, onSuccess, projectId, toast, materials,
  boqItems, subsections, categories, suppliers,
  accountLedgers, expenseCategories, costCategories,
  viewOnly = false, transaction = null
}) {
  const [form, setForm] = useState({ transaction_date: new Date().toISOString().slice(0, 10), unit_of_measure: 'Nr', currency: 'NGN' });
  const [saving, setSaving] = useState(false);
  const [boqComponents, setBoqComponents] = useState([]);
  const [showAlternateItem, setShowAlternateItem] = useState(false);

  useEffect(() => {
    if (!viewOnly && !transaction) {
      // Auto-generate transaction ID: DMR-YYYYMMDD-XXXXX
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const randomStr = Math.random().toString(36).substring(2, 7).toUpperCase();
      setForm(f => ({ ...f, transaction_id: `DMR-${dateStr}-${randomStr}` }));
    }
  }, [viewOnly, transaction]);

  useEffect(() => {
    if (transaction) {
      setForm({
        ...transaction,
        transaction_date: transaction.transaction_date ? transaction.transaction_date.slice(0, 10) : ''
      });
      if (transaction.alternative_item_name || transaction.reason_for_using_alternative_item) {
        setShowAlternateItem(true);
      }
    }
  }, [transaction]);

  useEffect(() => {
    if (form.boq_item_id) {
      const selectedItem = boqItems.find(i => String(i.boq_item_id) === String(form.boq_item_id) || String(i.id) === String(form.boq_item_id));
      if (selectedItem && selectedItem.components && selectedItem.components.length > 0) {
        setBoqComponents(selectedItem.components);
      } else {
        setBoqComponents([]);
      }
    } else {
      setBoqComponents([]);
    }
  }, [form.boq_item_id, boqItems]);

  // Frontend Calculations
  useEffect(() => {
    if (!viewOnly) {
      const qty = parseFloat(form.quantity) || 0;
      const price = parseFloat(form.unit_price) || 0;
      const discount = parseFloat(form.discount_amount) || 0;

      let baseAmount = (qty * price) - discount;

      const vatPercent = parseFloat(form.vat_percent) || 0;
      const whtPercent = parseFloat(form.wht_percent) || 0;
      const retention = parseFloat(form.retention_amount) || 0;
      const mgtFee = parseFloat(form.management_fee) || 0;
      const other = parseFloat(form.other_charges) || 0;

      const vatAmount = baseAmount * (vatPercent / 100);
      const whtAmount = baseAmount * (whtPercent / 100);

      const finalTotal = baseAmount + vatAmount + other + mgtFee - whtAmount - retention;

      if (form.total_amount !== finalTotal) {
        setForm(f => ({ ...f, total_amount: finalTotal.toFixed(2) }));
      }
    }
  }, [
    form.quantity, form.unit_price, form.discount_amount,
    form.vat_percent, form.wht_percent, form.retention_amount,
    form.management_fee, form.other_charges
  ]);

  const saveTransaction = async () => {
    if (!form.material_id || !form.quantity || !form.unit_price || !form.dmr_subsection_id || !form.dmr_category_id) {
      toast.show('Please fill in required fields: Sub-section, Category, Material, Quantity, Unit Price', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, project_id: projectId };
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') payload[key] = null;
      });
      if (transaction && !viewOnly) {
        await api.updateMaterialTxn(transaction.id, payload);
        toast.show('Draft material entry updated successfully');
      } else {
        await api.createMaterialTxn(payload);
        toast.show('Draft material entry created successfully');
      }
      if (onSuccess) onSuccess(!!(transaction && !viewOnly));
    } catch (e) {
      toast.show(e.message, 'error');
    }
    setSaving(false);
  };

  const getMaterialName = (id) => materials.find(m => m.material_id === id)?.name || id;

  // Shared Styles to match UI design
  const modalContainerStyle = { padding: 0, overflow: 'hidden', background: '#ffffff', borderRadius: 12 };
  const headerStyle = { background: '#0b318bff', padding: '12px 24px', color: '#ffffff', position: 'relative' };
  const footerStyle = { background: '#0b318bff', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
  const sectionTitleStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', marginBottom: 12, textTransform: 'uppercase' };
  const inputStyle = { background: '#e2e8f0', color: '#1e293b', border: '1px solid #cbd5e1', padding: '10px 12px' };

  if (viewOnly && transaction) {
    const getMaterialName = (id) => materials.find(m => m.material_id === id)?.name || id;
    const getSubName = (id) => subsections.find(s => s.id === id)?.name || id;
    const getCatName = (id) => categories.find(c => c.id === id)?.name || id;
    const getSupplierName = (id) => suppliers.find(s => s.supplier_id === id)?.name || id;
    const getCostCatName = (id) => costCategories.find(c => c.cost_category_id === id)?.name || id;
    const getAccountLedgerName = (id) => accountLedgers.find(a => a.ledger_id === id)?.name || id;
    const getExpenseCategoryName = (id) => expenseCategories.find(e => e.expense_category_id === id)?.name || id;

    const getBoqItemName = (id) => {
      const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
      const item = itemsArray.find(b => String(b.boq_item_id) === String(id) || String(b.id) === String(id));
      return item ? `${item.boq_code || ''} ${item.description}`.trim() : id;
    };

    const getBoqCompName = (boqId, compId) => {
      if (!compId) return '—';
      const itemsArray = Array.isArray(boqItems) ? boqItems : (boqItems?.data || boqItems?.items || []);
      const item = itemsArray.find(b => String(b.boq_item_id) === String(boqId) || String(b.id) === String(boqId));
      if (item && item.components) {
        const compsArray = Array.isArray(item.components) ? item.components : (item.components?.data || item.components?.items || []);
        const comp = compsArray.find(c => String(c.component_id) === String(compId) || String(c.id) === String(compId));
        if (comp) return comp.description || comp.name || compId;
      }
      return compId;
    };

    const ViewField = ({ label, value, span }) => (
      <div style={{ display: 'flex', flexDirection: 'column', gridColumn: span ? `span ${span}` : undefined }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>{label}</span>
        <div style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '9px 12px', fontSize: 13, fontWeight: 600, color: '#1e293b', minHeight: '38px', display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
          {value !== null && value !== undefined && value !== '' ? value : '—'}
        </div>
      </div>
    );

    return (
      <Modal onClose={onClose} width={1500} hideHeader>
        <div style={modalContainerStyle}>
          <div style={headerStyle}>
            <button onClick={onClose} aria-label="Close" style={{
              position: 'absolute', top: 16, right: 16,
              background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
              fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(240, 57, 91, 0.6)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'scale(1)'; }}
            >×</button>
            <h2 style={{ margin: '0 0 3px 0', fontSize: 15, fontWeight: 700, color: '#ffffff' }}>View Direct Material Entry</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Badge color={transaction.status === 'DRAFT' ? 'gray' : 'blue'}>{transaction.status}</Badge>
            </div>
          </div>

          <div style={{ padding: '24px', background: '#ffffff', maxHeight: '70vh', overflowY: 'auto', borderLeft: '1px solid black', borderRight: '1px solid black' }}>

            {/* SECTION 1 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 1 — BOQ LINE CLASSIFICATION</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="BOQ ITEM" value={getBoqItemName(transaction.boq_item_id)} />
                <ViewField label="BOQ COMPONENT" value={getBoqCompName(transaction.boq_item_id, transaction.boq_component_id)} />
                <ViewField label="COST COMPONENT" value={getCostCatName(transaction.cost_category_id)} />
              </div>
            </div>

            {/* SECTION 2 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS</div>
              <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <ViewField label="ENTRY DATE" value={transaction.transaction_date ? fmtDate(transaction.transaction_date) : ''} />
                <div />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>TRANSACTION ID</label>
                  <div style={{ background: '#e2e8f0', border: '2px dashed #081220ff', borderRadius: '6px', padding: '9px 12px', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 15, fontWeight: 700, color: '#16a34a' }}>
                      {transaction.transaction_id || '—'}
                    </span>
                  </div>
                </div>

                <ViewField label="PAYMENT MODE" value={transaction.payment_mode} />
                <ViewField label="SUB-SECTION" value={getSubName(transaction.dmr_subsection_id)} />
                <ViewField label="CATEGORY" value={getCatName(transaction.dmr_category_id)} />

                <ViewField label="SUPPLIER" value={getSupplierName(transaction.supplier_id)} />
                <ViewField label="MANUAL NO" value={transaction.manual_no} />
                <ViewField label="INVOICE NO" value={transaction.invoice_no} />

                <ViewField label="WAYBILL NO" value={transaction.waybill_no} />
                <ViewField label="VOUCHER NO" value={transaction.voucher_no} />
                <ViewField label="REFERENCE NO" value={transaction.reference_no} />
              </div>
            </div>

            {/* SECTION 3 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={sectionTitleStyle}>SECTION 3 — MATERIAL LINE & FINANCIALS</div>
              <div className="grid-cols-4" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                <div style={{ gridColumn: 'span 2' }}><ViewField label="MATERIAL" value={getMaterialName(transaction.material_id)} /></div>
                <ViewField label="ACCOUNT LEDGER" value={getAccountLedgerName(transaction.account_ledger_id)} />
                <ViewField label="EXPENSE CODE" value={getExpenseCategoryName(transaction.expense_category_id)} />

                <ViewField label="QUANTITY" value={transaction.quantity ? fmtNum(transaction.quantity) : ''} />
                <ViewField label="UNIT OF MEASURE" value={transaction.unit_of_measure} />
                <ViewField label="UNIT PRICE" value={transaction.unit_price ? fmt(transaction.unit_price) : ''} />
                <ViewField label="CURRENCY" value={transaction.currency} />

                <ViewField label="DISCOUNT AMT" value={transaction.discount_amount ? fmt(transaction.discount_amount) : ''} />
                <ViewField label="VAT %" value={transaction.vat_percent} />
                <ViewField label="VAT WITHHELD" value={transaction.vat_witheld ? fmt(transaction.vat_witheld) : ''} />
                <ViewField label="WHT %" value={transaction.wht_percent} />

                <ViewField label="RETENTION AMT" value={transaction.retention_amount ? fmt(transaction.retention_amount) : ''} />
                <ViewField label="MANAGEMENT FEE" value={transaction.management_fee ? fmt(transaction.management_fee) : ''} />
                <ViewField label="OTHER CHARGES" value={transaction.other_charges ? fmt(transaction.other_charges) : ''} />
                <ViewField label="USD AMOUNT" value={transaction.usd_amount ? fmtNum(transaction.usd_amount) : ''} />

                <ViewField label="BRAND NAME" value={transaction.brand_name} span={2} />

                <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>TOTAL AMOUNT</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmt(transaction.total_amount || 0)}</span>
                </div>
              </div>
            </div>

            {/* SECTION 4 */}
            <div>
              <div style={sectionTitleStyle}>SECTION 4 — ALTERNATIVE ITEM & REMARKS</div>
              <div className="grid-cols-2" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
                {(transaction.alternative_item_name || transaction.reason_for_using_alternative_item) && (
                  <>
                    <ViewField label="ALTERNATIVE ITEM NAME" value={transaction.alternative_item_name} />
                    <ViewField label="REASON FOR USING ALTERNATIVE" value={transaction.reason_for_using_alternative_item} />
                  </>
                )}
                <ViewField label="REMARKS" value={transaction.remarks} span={2} />
              </div>
            </div>
          </div>

          <div style={footerStyle}>
            <div />
            <Btn onClick={onClose} style={{ background: '#475569', color: '#fff', border: 'none' }}>Close</Btn>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} width={1500} hideHeader>
      <div style={modalContainerStyle}>

        {/* HEADER */}
        <div style={headerStyle}>
          <button onClick={onClose} aria-label="Close" style={{
            position: 'absolute', top: 16, right: 16,
            background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
            fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(240, 57, 91, 0.6)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'scale(1)'; }}
          >×</button>
          <h2 style={{ margin: '0 0 3px 0', fontSize: 15, fontWeight: 700, color: '#ffffff' }}>{transaction && !viewOnly ? 'Edit Direct Material Entry' : 'New Direct Material Entry'}</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <p style={{ margin: 0, fontSize: 10, color: '#94a3b8' }}>Main BOQ Code + Cost Component are required · Default component: MAT — Landed Cost of Materials.</p>
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ padding: '24px', background: '#ffffff', maxHeight: '70vh', overflowY: 'auto', borderLeft: '1px solid black', borderRight: '1px solid black' }}>

          {/* SECTION 1 */}
          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 1 — BOQ LINE CLASSIFICATION</div>
            <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <Select label="MAIN BOQ ITEM" value={form.boq_item_id || ''}
                onChange={v => setForm(f => ({ ...f, boq_item_id: v, boq_component_id: '' }))}
                style={inputStyle}
                options={[{ value: '', label: '— Select BOQ Item —' }, ...boqItems.map(i => ({ value: i.boq_item_id, label: `${i.boq_code || ''} — ${i.description}`.trim() }))]} />

              <Select label="SUB BOQ COMPONENT" value={form.boq_component_id || ''}
                onChange={v => setForm(f => ({ ...f, boq_component_id: v }))}
                style={inputStyle}
                disabled={boqComponents.length === 0}
                options={boqComponents.length === 0
                  ? [{ value: '', label: '— No Components Available —' }]
                  : [{ value: '', label: '— Select Component —' }, ...boqComponents.map(c => ({ value: c.component_id, label: c.description }))]
                } />

              <Select label="COST COMPONENT" value={form.cost_category_id || ''}
                onChange={v => setForm(f => ({ ...f, cost_category_id: v }))}
                style={inputStyle}
                options={[{ value: '', label: '— Select Cost Component —' }, ...(costCategories || []).map(c => ({ value: c.cost_category_id, label: c.name }))]} />
            </div>
          </div>

          {/* SECTION 2 */}
          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 2 — GENERAL DETAILS</div>
            <div className="grid-cols-3" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              {/* ROW 1 */}
              <DatePicker label="ENTRY DATE" required value={form.transaction_date || ''}
                onChange={v => setForm(f => ({ ...f, transaction_date: v }))} style={inputStyle} />

              <div />

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 'bold', color: '#000101ff', marginBottom: 4, textTransform: 'uppercase' }}>TRANSACTION ID</label>
                <div style={{ background: '#e2e8f0', border: '2px dashed #081220ff', borderRadius: '6px', padding: '9px 12px', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                    {form.transaction_id || 'Generating ID...'}
                  </span>
                </div>
              </div>

              {/* ROW 2 */}
              <Select label="PAYMENT MODE" value={form.payment_mode || ''}
                onChange={v => setForm(f => ({ ...f, payment_mode: v }))}
                style={inputStyle}
                options={[
                  { value: '', label: '— Select Payment Mode —' },
                  { value: 'Bank Transfer', label: 'Bank Transfer' },
                  { value: 'Cash', label: 'Cash' },
                  { value: 'Cheque', label: 'Cheque' },
                  { value: 'POS', label: 'POS' },
                  { value: 'Mobile money', label: 'Mobile money' },
                  { value: 'Credit (30 days)', label: 'Credit (30 days)' },
                  { value: 'Credit (60 days)', label: 'Credit (60 days)' },
                ]} />
              <Select label="SUB-SECTION" required value={form.dmr_subsection_id || ''}
                onChange={v => setForm(f => ({ ...f, dmr_subsection_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Sub-section —' }, ...subsections.map(b => ({ value: b.id, label: b.name }))]} />
              <Select label="CATEGORY" required value={form.dmr_category_id || ''}
                onChange={v => setForm(f => ({ ...f, dmr_category_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Category —' }, ...categories.map(c => ({ value: c.id, label: c.name }))]} />

              {/* ROW 3 */}
              <Select label="SUPPLIER" value={form.supplier_id || ''}
                onChange={v => setForm(f => ({ ...f, supplier_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Supplier —' }, ...(suppliers || []).map(s => ({ value: s.supplier_id, label: s.name }))]} />
              <Input label="MANUAL NO" value={form.manual_no || ''}
                onChange={v => setForm(f => ({ ...f, manual_no: v }))} style={inputStyle} />
              <Input label="INVOICE NO" value={form.invoice_no || ''}
                onChange={v => setForm(f => ({ ...f, invoice_no: v }))} style={inputStyle} />

              {/* ROW 4 */}
              <Input label="WAYBILL NO" value={form.waybill_no || ''}
                onChange={v => setForm(f => ({ ...f, waybill_no: v }))} style={inputStyle} />
              <Input label="VOUCHER NO" value={form.voucher_no || ''}
                onChange={v => setForm(f => ({ ...f, voucher_no: v }))} style={inputStyle} />
              <Input label="REFERENCE NO" value={form.reference_no || ''}
                onChange={v => setForm(f => ({ ...f, reference_no: v }))} style={inputStyle} />
            </div>
          </div>

          {/* SECTION 3 */}
          <div style={{ marginBottom: '32px' }}>
            <div style={sectionTitleStyle}>SECTION 3 — MATERIAL LINE & FINANCIALS</div>
            <div className="grid-cols-4" style={{ background: '#f0f4f8', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              <Select label="MATERIAL" required value={form.material_id || ''}
                onChange={v => setForm(f => ({ ...f, material_id: v }))}
                style={{ ...inputStyle, gridColumn: 'span 2' }}
                options={[{ value: '', label: '— Select Material —' }, ...materials.map(m => ({ value: m.material_id, label: m.name }))]} />
              <Select label="ACCOUNT LEDGER" value={form.account_ledger_id || ''}
                onChange={v => setForm(f => ({ ...f, account_ledger_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Account Ledger —' }, ...(accountLedgers || []).map(a => ({ value: a.ledger_id, label: a.name }))]} />
              <Select label="EXPENSE CODE" value={form.expense_category_id || ''}
                onChange={v => setForm(f => ({ ...f, expense_category_id: v }))} style={inputStyle}
                options={[{ value: '', label: '— Select Expense Code —' }, ...(expenseCategories || []).map(e => ({ value: e.expense_category_id, label: e.name }))]} />

              <Input label="QUANTITY" type="number" required value={form.quantity || ''}
                onChange={v => setForm(f => ({ ...f, quantity: v }))} style={inputStyle} />
              <Select label="UNIT OF MEASURE" required value={form.unit_of_measure || 'Nr'}
                onChange={v => setForm(f => ({ ...f, unit_of_measure: v }))} style={inputStyle}
                options={['M', 'M2', 'M3', 'Nr', 'KG'].map(s => ({ value: s, label: s }))} />
              <Input label="UNIT PRICE" type="number" required value={form.unit_price || ''}
                onChange={v => setForm(f => ({ ...f, unit_price: v }))} style={inputStyle} />
              <Select label="CURRENCY" value={form.currency || 'NGN'}
                onChange={v => setForm(f => ({ ...f, currency: v }))} style={inputStyle}
                options={['NGN', 'USD', 'EUR', 'GBP'].map(s => ({ value: s, label: s }))} />

              <Input label="DISCOUNT AMT" type="number" value={form.discount_amount || ''}
                onChange={v => setForm(f => ({ ...f, discount_amount: v }))} style={inputStyle} />
              <Input label="VAT %" type="number" value={form.vat_percent || ''}
                onChange={v => setForm(f => ({ ...f, vat_percent: v }))} style={inputStyle} />
              <Input label="VAT WITHHELD" type="number" value={form.vat_witheld || ''}
                onChange={v => setForm(f => ({ ...f, vat_witheld: v }))} style={inputStyle} />
              <Input label="WHT %" type="number" value={form.wht_percent || ''}
                onChange={v => setForm(f => ({ ...f, wht_percent: v }))} style={inputStyle} />

              <Input label="RETENTION AMT" type="number" value={form.retention_amount || ''}
                onChange={v => setForm(f => ({ ...f, retention_amount: v }))} style={inputStyle} />
              <Input label="MANAGEMENT FEE" type="number" value={form.management_fee || ''}
                onChange={v => setForm(f => ({ ...f, management_fee: v }))} style={inputStyle} />
              <Input label="OTHER CHARGES" type="number" value={form.other_charges || ''}
                onChange={v => setForm(f => ({ ...f, other_charges: v }))} style={inputStyle} />
              <Input label="USD AMOUNT" type="number" value={form.usd_amount || ''}
                onChange={v => setForm(f => ({ ...f, usd_amount: v }))} style={inputStyle} />

              <Input label="BRAND NAME" value={form.brand_name || ''}
                onChange={v => setForm(f => ({ ...f, brand_name: v }))} style={{ ...inputStyle, gridColumn: 'span 2' }} />

              <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#1e293b', borderRadius: '8px', padding: '12px 16px', color: '#fff' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>CALCULATED TOTAL AMOUNT</span>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#10b981', fontFamily: 'var(--mono)' }}>{fmt(form.total_amount || 0)}</span>
              </div>
            </div>
          </div>

          {/* SECTION 4 */}
          <div>
            <div style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span>SECTION 4 — ALTERNATIVE ITEM & REMARKS</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textTransform: 'none', color: '#1e293b', cursor: 'pointer' }}>
                <input type="checkbox" checked={showAlternateItem} onChange={e => setShowAlternateItem(e.target.checked)} />
                Include Alternate Item Details
              </label>
            </div>

            <div className="grid-cols-2" style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', gap: '16px', border: '1px solid black' }}>
              {showAlternateItem && (
                <>
                  <Input label="ALTERNATIVE ITEM NAME" value={form.alternative_item_name || ''}
                    onChange={v => setForm(f => ({ ...f, alternative_item_name: v }))} style={inputStyle} />
                  <Input label="REASON FOR USING ALTERNATIVE" value={form.reason_for_using_alternative_item || ''}
                    onChange={v => setForm(f => ({ ...f, reason_for_using_alternative_item: v }))} style={inputStyle} />
                </>
              )}

              <div style={{ gridColumn: 'span 2' }}>
                <Textarea label="REMARKS" value={form.remarks || ''}
                  onChange={v => setForm(f => ({ ...f, remarks: v }))}
                  rows={3} placeholder="Optional notes or description about the entry..."
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div style={footerStyle}>
          <Btn style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }} onClick={onClose}>
            Cancel
          </Btn>
          <Btn loading={saving} onClick={saveTransaction} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '10px 24px', fontWeight: 600, fontSize: 14 }}>
            {transaction && !viewOnly ? 'Update' : 'Save'}
          </Btn>
        </div>

      </div>
    </Modal>
  );
}
